import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  Grid,
  Link as LinkMui,
  TableCell,
  TableRow,
  TableHead,
  Table,
  Typography,
  TableBody,
  Container,
  Skeleton,
} from '@mui/material'
import { Link as LinkRouter, useParams, useNavigate } from 'react-router-dom'
import { ChevronLeft, ExpandMore } from '@mui/icons-material'
import { customerCreditType, customerCreditLineType, invoiceType } from '../../types'
import { useContext, useEffect, useState } from 'react'
import axios from 'axios'
import dayjs from 'dayjs'
import './Credit.css'
import ErrorScreen from '../../Misc/ErrorScreen'
import { dataContext } from '../..'

function Credit() {
  type creditLine = customerCreditLineType & {
    item_name: string
  }
  type creditDetail = customerCreditType & {
    name: string
    parent_customer_name: string
  }
  const { id } = useParams()
  const { user } = useContext(dataContext)
  const navigate = useNavigate()
  const [credit, setCredit] = useState<creditDetail | null>(null)
  const [creditLines, setCreditLines] = useState<creditLine[]>([])
  const [invoices, setInvoices] = useState<invoiceType[]>([])
  const [lineItemsExpanded, setLineItemsExpanded] = useState(true)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<'ok' | 'error' | 'unauthorized'>('ok')

  useEffect(() => {
    if (id && user.active_flag) {
      setLoading(true)
      setLoadError('ok')
      axios
        .get(`/credit/${id}?${user.user_type === 'Sales Rep' ? `salesUser=${user.user_id}` : ''}`)
        .then(res => {
          setCredit(res.data.customer_credit)
          setCreditLines(res.data.customer_credit_lines)
          setInvoices(res.data.related_invoices)
          setLoadError('ok')
        })
        .catch(err => {
          console.error(err)
          if (err.response.status === 401) {
            setLoadError('unauthorized')
          } else {
            setLoadError('error')
          }
        })
        .finally(() => setLoading(false))
    }
  }, [user])

  useEffect(() => {
    document.title = `Customer Credit ${credit?.ref_number ? `| ${credit.ref_number}` : ''} | Salestrack`
  }, [credit])

  return (
    <div className="credit-detail">
      <div className="credit-detail-button-container">
        <Button
          size="large"
          startIcon={<ChevronLeft />}
          variant="contained"
          className="credit-detail-button"
          component={LinkRouter}
          to="/customer/credits"
          loading={loading}
          onClick={e => {
            e.preventDefault()
            navigate(-1)
          }}
        >
          Return
        </Button>
        {loading ? (
          <Skeleton width={220} height={32} />
        ) : (
          <Chip label={<Typography variant="h6">Credit #: {credit?.ref_number}</Typography>} />
        )}
      </div>
      {loadError !== 'ok' ? (
        <div className="credit-detail-error-container">
          <ErrorScreen
            errorText={
              loadError === 'unauthorized'
                ? 'You are not authorized to view this credit.'
                : 'Failed to load credit details. Please try again or contact support if the error persists.'
            }
            title="Credit Load Failed"
          />
        </div>
      ) : (
        <div className="credit-detail-data-container">
          <div className="credit-detail-data-container-header">
            <Container className="credit-detail-header-container" maxWidth={false}>
              <Container className="credit-detail-header-column" maxWidth={false}>
                <Grid className="credit-detail-header-grid">
                  <Typography variant="body2" color="text.secondary">
                    {loading ? <Skeleton width={100} /> : 'Customer Name:'}
                  </Typography>
                  <Typography variant="body1">
                    {loading ? (
                      <Skeleton width={120} />
                    ) : credit?.parent_customer_name ? (
                      credit.parent_customer_name
                    ) : credit?.name ? (
                      credit.name
                    ) : (
                      '-'
                    )}
                  </Typography>
                </Grid>
                <Grid className="credit-detail-header-grid">
                  <Typography variant="body2" color="text.secondary">
                    {loading ? <Skeleton width={80} /> : 'Job Name:'}
                  </Typography>
                  <Typography variant="body1">
                    {loading ? <Skeleton width={100} /> : credit?.name ? credit.name : '-'}
                  </Typography>
                </Grid>
                <Grid className="credit-detail-header-grid">
                  <Typography variant="body2" color="text.secondary">
                    {loading ? <Skeleton width={90} /> : 'Customer PO:'}
                  </Typography>
                  <Typography variant="body1">
                    {loading ? <Skeleton width={120} /> : credit?.customer_po_number ? credit.customer_po_number : '-'}
                  </Typography>
                </Grid>
                <Grid className="credit-detail-header-grid">
                  <Typography variant="body2" color="text.secondary">
                    {loading ? <Skeleton width={110} /> : 'Customer Phone:'}
                  </Typography>
                  <Typography variant="body1">
                    {loading ? <Skeleton width={100} /> : credit?.contact_phone ? credit.contact_phone : '-'}
                  </Typography>
                </Grid>
              </Container>
              <Container className="credit-detail-header-column" maxWidth={false}>
                <Grid className="credit-detail-header-grid">
                  <Typography variant="body2" color="text.secondary">
                    {loading ? <Skeleton width={110} /> : 'Customer Email:'}
                  </Typography>
                  <Typography variant="body1">
                    {loading ? <Skeleton width={150} /> : credit?.contact_email ? credit.contact_email : '-'}
                  </Typography>
                </Grid>
                <Grid className="credit-detail-header-grid">
                  <Typography variant="body2" color="text.secondary">
                    {loading ? <Skeleton width={90} /> : 'Credit Date:'}
                  </Typography>
                  <Typography variant="body1">
                    {loading ? (
                      <Skeleton width={100} />
                    ) : credit?.txn_date ? (
                      dayjs.utc(credit.txn_date).format('MM/DD/YYYY')
                    ) : (
                      '-'
                    )}
                  </Typography>
                </Grid>
                <Grid className="credit-detail-header-grid">
                  <Typography variant="body2" color="text.secondary">
                    {loading ? <Skeleton width={80} /> : 'Due Date:'}
                  </Typography>
                  <Typography variant="body1">
                    {loading ? (
                      <Skeleton width={100} />
                    ) : credit?.due_date ? (
                      dayjs.utc(credit.due_date).format('MM/DD/YYYY')
                    ) : (
                      '-'
                    )}
                  </Typography>
                </Grid>
                <Grid className="credit-detail-header-grid">
                  <Typography variant="body2" color="text.secondary">
                    {loading ? <Skeleton width={100} /> : 'Total Amount:'}
                  </Typography>
                  <Typography variant="body1">
                    {loading ? (
                      <Skeleton width={100} />
                    ) : credit?.total_amount ? (
                      credit.total_amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                    ) : (
                      '-'
                    )}
                  </Typography>
                </Grid>
              </Container>
              <Container className="credit-detail-header-column" maxWidth={false}>
                <Grid className="credit-detail-header-grid">
                  <Typography variant="body2" color="text.secondary">
                    {loading ? <Skeleton width={130} /> : 'Bill Address Street:'}
                  </Typography>
                  <Typography variant="body1">
                    {loading ? <Skeleton width={150} /> : credit?.bill_address_addr1 ? credit.bill_address_addr1 : '-'}
                  </Typography>
                </Grid>
                <Grid className="credit-detail-header-grid">
                  <Typography variant="body2" color="text.secondary">
                    {loading ? <Skeleton width={120} /> : 'Bill Address City:'}
                  </Typography>
                  <Typography variant="body1">
                    {loading ? <Skeleton width={100} /> : credit?.bill_address_city ? credit.bill_address_city : '-'}
                  </Typography>
                </Grid>
                <Grid className="credit-detail-header-grid">
                  <Typography variant="body2" color="text.secondary">
                    {loading ? <Skeleton width={130} /> : 'Bill Address State:'}
                  </Typography>
                  <Typography variant="body1">
                    {loading ? <Skeleton width={80} /> : credit?.bill_address_state ? credit.bill_address_state : '-'}
                  </Typography>
                </Grid>
              </Container>
            </Container>
          </div>
          <div className="credit-detail-data-container-line-items">
            <Accordion
              onChange={() => {
                if (lineItemsExpanded) {
                  setLineItemsExpanded(false)
                } else {
                  setTimeout(() => {
                    setLineItemsExpanded(true)
                  }, 600)
                }
              }}
              defaultExpanded
            >
              <AccordionSummary
                expandIcon={<ExpandMore className="MuiAccordionSummary-expandIcon" />}
                className="credit-accordion-summary"
              >
                {loading ? (
                  <Skeleton width={220} height={32} />
                ) : (
                  <Typography variant="h6">Credit Line Items</Typography>
                )}
              </AccordionSummary>
              <AccordionDetails className="credit-accordion-details">
                <Table color="primary">
                  <TableHead className={lineItemsExpanded ? 'table-head-expanded' : ''}>
                    {loading ? (
                      <TableRow>
                        {Array.from({ length: 6 }).map((_, idx) => (
                          <TableCell key={idx}>
                            <Skeleton className="skeleton-header" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ) : (
                      <TableRow>
                        <TableCell>SI. No.</TableCell>
                        <TableCell>Item Name</TableCell>
                        <TableCell width="40%">Description</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell align="right">Rate</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    )}
                  </TableHead>
                  <TableBody>
                    {loading
                      ? Array.from({ length: 5 }).map((_, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <Skeleton width={40} />
                            </TableCell>
                            <TableCell>
                              <Skeleton width={120} />
                            </TableCell>
                            <TableCell>
                              <Skeleton width={200} />
                            </TableCell>
                            <TableCell align="right">
                              <Skeleton width={60} />
                            </TableCell>
                            <TableCell align="right">
                              <Skeleton width={80} />
                            </TableCell>
                            <TableCell align="right">
                              <Skeleton width={100} />
                            </TableCell>
                          </TableRow>
                        ))
                      : creditLines.map(line => (
                          <TableRow key={line.customer_credit_line_txn_id}>
                            <TableCell>{line.line_numerical_order}</TableCell>
                            <TableCell>{line.item_name ? line.item_name : '-'}</TableCell>
                            <TableCell>{line.item_desc ? line.item_desc : '-'}</TableCell>
                            <TableCell align="right">{line.quantity ? line.quantity : '-'}</TableCell>
                            <TableCell align="right">
                              {line.rate
                                ? line.rate.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                                : '-'}
                            </TableCell>
                            <TableCell align="right">
                              {line.amount
                                ? line.amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                                : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          </div>
          <div className="credit-detail-data-container-related-items">
            {loading ? <Skeleton width={180} height={32} /> : <Typography variant="h6">Related Items</Typography>}
            <Table color="primary">
              <TableHead>
                {loading ? (
                  <TableRow>
                    {Array.from({ length: 6 }).map((_, idx) => (
                      <TableCell key={idx}>
                        <Skeleton className="skeleton-header" />
                      </TableCell>
                    ))}
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Reference Number</TableCell>
                    <TableCell align="right">Transaction Date</TableCell>
                    <TableCell align="right">Due Date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                )}
              </TableHead>
              <TableBody>
                {loading
                  ? Array.from({ length: 3 }).map((_, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Skeleton width={120} />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton width={100} />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton width={100} />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton width={100} />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton width={100} />
                        </TableCell>
                        <TableCell>
                          <Skeleton width={80} />
                        </TableCell>
                      </TableRow>
                    ))
                  : invoices.map(invoice => (
                      <TableRow key={invoice.invoice_txn_id}>
                        <TableCell>Invoice</TableCell>
                        <TableCell align="right">
                          <LinkMui to={`/customer/invoice/${invoice.invoice_txn_id}`} component={LinkRouter}>
                            {invoice.ref_number}
                          </LinkMui>
                        </TableCell>
                        <TableCell align="right">
                          {invoice.txn_date ? dayjs.utc(invoice.txn_date).format('MM/DD/YYYY') : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {invoice.due_date ? dayjs.utc(invoice.due_date).format('MM/DD/YYYY') : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {invoice.applied_amount
                            ? invoice.applied_amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                            : '-'}
                        </TableCell>
                        <TableCell>{invoice.is_paid ? 'Paid' : 'Not Paid'}</TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  )
}

export default Credit
